/**
 * Cache pro panoramata z mapy.cz API
 * Optimalizace: uklada vysledky SMap.Pano.getBest() abychom nedelali duplicitni requesty
 */

interface PanoramaCacheEntry {
    place: any; // SMap panorama place objekt nebo nový formát {lat, lon, date?}
    timestamp: number;
    tolerance: number;
}

interface CoordinateKey {
    lat: number;
    lon: number;
}

// Cache expirace - 30 minut (panoramata se nemeni casto)
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

// Maximalni velikost cache (abychom nezabírali moc pameti)
const MAX_CACHE_SIZE = 100;

// Tolerance pro porovnani souradnic (v metrech prepocteno na stupne)
// Priblizne 50m = 0.00045 stupne
const COORDINATE_TOLERANCE = 0.0005;

class PanoramaCache {
    private cache: Map<string, PanoramaCacheEntry> = new Map();
    private usedPanoramasInSession: Set<string> = new Set();

    /**
     * Generuje klic pro cache z koordinat
     */
    private generateKey(lat: number, lon: number, tolerance: number): string {
        // Zaokrouhlime na 4 desetinna mista pro lepsi cache hit rate
        const roundedLat = Math.round(lat * 10000) / 10000;
        const roundedLon = Math.round(lon * 10000) / 10000;
        return `${roundedLat},${roundedLon},${tolerance}`;
    }

    /**
     * Generuje jednoduchy klic bez tolerance pro sledovani pouzitych panoramat
     */
    private generateSimpleKey(lat: number, lon: number): string {
        const roundedLat = Math.round(lat * 10000) / 10000;
        const roundedLon = Math.round(lon * 10000) / 10000;
        return `${roundedLat},${roundedLon}`;
    }

    /**
     * Zkontroluje jestli je panorama v cache a jestli nevyprselo
     */
    get(lat: number, lon: number, tolerance: number): any | null {
        const key = this.generateKey(lat, lon, tolerance);
        const entry = this.cache.get(key);

        if (!entry) {
            return null;
        }

        // Kontrola expirace
        if (Date.now() - entry.timestamp > CACHE_EXPIRATION_MS) {
            this.cache.delete(key);
            return null;
        }

        return entry.place;
    }

    /**
     * Hleda nejblizsi cachovane panorama v okruhu dane tolerance
     */
    findNearby(lat: number, lon: number, maxTolerance: number): any | null {
        const now = Date.now();
        let result: any | null = null;
        const keysToDelete: string[] = [];

        this.cache.forEach((entry, key) => {
            // Preskocime expirovane
            if (now - entry.timestamp > CACHE_EXPIRATION_MS) {
                keysToDelete.push(key);
                return;
            }

            // Parsujeme koordinaty z klice
            const [cachedLat, cachedLon, cachedTolerance] = key.split(',').map(Number);

            // Pocitame vzdalenost (aproximace)
            const latDiff = Math.abs(lat - cachedLat);
            const lonDiff = Math.abs(lon - cachedLon);

            // Pokud je v toleranci a cachovana tolerance je mensi nebo stejna
            if (
                latDiff < COORDINATE_TOLERANCE &&
                lonDiff < COORDINATE_TOLERANCE &&
                cachedTolerance <= maxTolerance &&
                !result
            ) {
                result = entry.place;
            }
        });

        // Smazeme expirovane zaznamy
        keysToDelete.forEach(key => this.cache.delete(key));

        return result;
    }

    /**
     * Ulozi panorama do cache
     */
    set(lat: number, lon: number, tolerance: number, place: any): void {
        // Cleanup pokud je cache moc velka
        if (this.cache.size >= MAX_CACHE_SIZE) {
            this.cleanup();
        }

        const key = this.generateKey(lat, lon, tolerance);
        this.cache.set(key, {
            place,
            timestamp: Date.now(),
            tolerance,
        });
    }

    /**
     * Oznaci panorama jako pouzite v aktualnim hracim session
     */
    markAsUsed(lat: number, lon: number): void {
        const key = this.generateSimpleKey(lat, lon);
        this.usedPanoramasInSession.add(key);
    }

    /**
     * Zkontroluje jestli bylo panorama uz pouzito v session
     */
    wasUsedInSession(lat: number, lon: number): boolean {
        const key = this.generateSimpleKey(lat, lon);
        return this.usedPanoramasInSession.has(key);
    }

    /**
     * Resetuje session (pro novou hru)
     */
    clearSession(): void {
        this.usedPanoramasInSession.clear();
    }

    /**
     * Vycisti expirovane zaznamy z cache
     */
    private cleanup(): void {
        const now = Date.now();
        const keysToDelete: string[] = [];

        this.cache.forEach((entry, key) => {
            if (now - entry.timestamp > CACHE_EXPIRATION_MS) {
                keysToDelete.push(key);
            }
        });

        // Smazeme expirovane
        keysToDelete.forEach((key) => this.cache.delete(key));

        // Pokud je stale moc velka, smazeme nejstarsi
        if (this.cache.size >= MAX_CACHE_SIZE) {
            const entries = Array.from(this.cache.entries());
            entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

            // Smazeme nejstarsi polovinu
            const toDelete = Math.floor(entries.length / 2);
            for (let i = 0; i < toDelete; i++) {
                this.cache.delete(entries[i][0]);
            }
        }
    }

    /**
     * Vycisti celou cache
     */
    clear(): void {
        this.cache.clear();
        this.usedPanoramasInSession.clear();
    }

    /**
     * Statistiky pro debugging
     */
    getStats(): { cacheSize: number; sessionUsed: number } {
        return {
            cacheSize: this.cache.size,
            sessionUsed: this.usedPanoramasInSession.size,
        };
    }
}

// Singleton instance
export const panoramaCache = new PanoramaCache();

// Export typu pro TypeScript
export type { PanoramaCacheEntry, CoordinateKey };
